import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";

import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import { Spinner } from "react-bootstrap";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../components/ui/Button";

import { DISPLAY_OPTIONS, } from "../../../Constants/apps";
import { COUNTRY_OPTIONS } from "../../../Constants/countryKey";

import { resetClockStates, setClockDisplay, setClockSettings, } from "../../../redux/Slices/Apps/appsSlice";
import { getFormErrorMessage } from "../../../config/global";
import { useCustomToast } from "../../Contexts/ToastContext";
import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import { TitleInput } from "../../SharedComponents/Clock/Inputs";


import CornerAreaControl from "../../SharedComponents/Weather/CornerAreaControl";
import { BgColorInput, CardColorInput, TextColorInput, } from "../../SharedComponents/Clock/ColorController";

import { useCreateClockMutation, useReadTimezones, useUpdateClockMutation } from "../../../queries";
import { useTranslation } from "react-i18next";
import { CLOCK_FORMAT, CLOCK_TYPES, FLIP_CLOCK_SETTINGS } from "../../../Constants/clock";

import CornerClassicClock from "../../SharedComponents/Clock/Classic/CornerClassicClock";
import CornerFlipClock from "../../SharedComponents/Clock/Flip/CornerFlipClock";
import CornerDigitalClock from "../../SharedComponents/Clock/Digital/CornerDigitalClock";
import CornerSimpleClock from "../../SharedComponents/Clock/Simple/CornerSimpleClock";

const defaultArea = {
  start_margin: 0,
  top_margin: 0,
  width: 35,
  height: 37,
  refresh: 0,
};

function ClockDialog() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showToast } = useCustomToast();
  const { clockSettings, clockDisplay, isEditingClock, clockData } = useSelector((state) => state.apps);
  const { openDialog } = useSelector((state) => state.global);

  const isEdit = isEditingClock;
  const [area, setArea] = useState(defaultArea);
  const [timeZoneData, setTimeZoneData] = useState();
  const [timestamp, setTimestamp] = useState();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [localLoading, setLocalLoading] = useState(true);

  const drawingWeatherAreaRef = useRef(null);

  const { formState: { errors }, handleSubmit, control, watch, reset, setValue } = useForm({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      title: isEdit ? clockData?.clock.title : "",
      country_code: isEdit ? clockData?.clock.country_code : "JO",
      display: isEdit ? clockDisplay : "FULL_SCREEN",
      clockType: isEdit ? clockData?.clock.clock_settings.clock_type : "FLIP",
      timeFormat: isEdit ? clockData?.clock.clock_settings.timeFormat : '24'
    },
  });

  const countryInput = watch("country_code");
  const clockTypeInput = watch("clockType");
  const timeZoneInput = watch("timeZone");
  const displayInput = watch("display");
  const timeFormatInput = watch("timeFormat");
  const createClock = useCreateClockMutation();
  const editClock = useUpdateClockMutation();
  const { mutate: readTimezones, data } = useReadTimezones();

  useEffect(() => {
    const updateDimensions = () => {
      if (drawingWeatherAreaRef.current) {
        const { offsetWidth, offsetHeight } = drawingWeatherAreaRef.current;
        setDimensions({ width: offsetWidth, height: offsetHeight - 10 });
      }
    };
    if (isEdit) {
      setArea(clockData?.clock?.clock_settings?.corner_setting ?? defaultArea);
    }

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);


  const formHandler = async (formData) => {
    const payload = {
      ...(isEdit && { id: clockData.clock.id }),
      title: formData?.title,
      display: displayInput,
      country_code: countryInput,
      zone_name: timeZoneInput ? timeZoneInput : data?.zones[0]?.zoneName,
      clock_settings: {
        ...clockSettings,
        ...(displayInput == "CORNER" ? { corner_setting: area } : { corner_setting: defaultArea })
      }
    };


    setLocalLoading(true);
    const mutation = isEdit ? editClock : createClock;
    await mutation.mutateAsync(payload, {
      onSuccess: (data) => {
        setLocalLoading(false)
        showToast('success', t('clock.clock_handling'), data?.msg);
      },
      onError: () => setLocalLoading(false),
    });
  };

  useEffect(() => {


    let payload = { country_code: countryInput };

    readTimezones(payload, {
      onSuccess: (data) => {
        if (drawingWeatherAreaRef.current) {
          const { offsetWidth, offsetHeight } = drawingWeatherAreaRef.current;
          setDimensions({ width: offsetWidth, height: offsetHeight - 10 });
        }
        setTimeZoneData(data?.zones)
        if (isEdit) {
          const matchedZone = data?.zones?.find(
            (zone) => zone.zoneName === clockData?.clock.zone_name
          );
          if (matchedZone) {
            setValue('timeZone', matchedZone.zoneName || '', { shouldDirty: true });
          }
        } else {
          setTimestamp(data?.zones[0].timestamp)
        }

        setLocalLoading(false);
      },
      onError: (error) => {
        console.error("Timezone API error:", error);
        setLocalLoading(false);
      },
    });
  }, [countryInput]);


  useEffect(() => {
    if (!timeZoneInput || !timeZoneData?.length) return;

    const matchedZone = timeZoneData.find(
      (zone) => zone.zoneName === timeZoneInput
    );

    if (matchedZone) {
      setTimestamp(matchedZone.timestamp);
    } else {
      console.warn("No matching timezone found for:", timeZoneInput);
    }
  }, [timeZoneInput]);


  const onClose = () => {
    dispatch(resetClockStates())
    dispatch(toggleDialog("ClockDialog"));
  };


  const handleMouseDown = (e, action) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;

    const initial = { ...area };

    const onMouseMove = (e) => {
      const dx = ((e.clientX - startX) / dimensions.width) * 100;
      const dy = ((e.clientY - startY) / dimensions.height) * 100;

      let updated = { ...initial };

      if (action === 'move') {
        updated.start_margin = Math.min(100 - initial.width, Math.max(0, initial.start_margin + dx));
        updated.top_margin = Math.min(100 - initial.height, Math.max(0, initial.top_margin + dy));
      }

      if (action === 'right') {
        updated.width = Math.min(100 - initial.start_margin, Math.max(5, initial.width + dx));
      }

      if (action === 'bottom') {
        updated.height = Math.min(100 - initial.top_margin, Math.max(5, initial.height + dy));
      }
      setArea(updated);

    }
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };


  return (
    <Dialog
      visible={openDialog.ClockDialog}
      style={{ width: "100%" }}
      header={isEdit ? t('clock.panel_modification') : t('clock.creation')}
      maximized
      blockScroll
      modal
      className="p-fluid"
      onHide={onClose}
    >
      <form
        onSubmit={handleSubmit(formHandler)}
        className="d-flex flex-column"
        encType="multipart/form-data"
      >
        <div className="col-12 h-[78vh] d-flex">
          <div className="col-7 auto-column my-5 px-3">
            <div
              ref={drawingWeatherAreaRef}
              className="d-flex weather_preview_container border-5 border-dark h-100"
            >
              {localLoading ? (
                <div className="justify-content-center align-items-center align-self-center w-100 text-center">
                  <Spinner
                    variant="primary"
                    as="span"
                    animation="border"
                    size="lg"
                    role="status"
                    className="m-3"
                    aria-hidden="true"
                  />
                </div>
              ) : data ? (
                <div style={{
                  width: `${dimensions.width}px`,
                  height: `${dimensions.height}px`,
                  overflow: "hidden",
                }}  >

                  {displayInput == "FULL_SCREEN" ?
                    <>
                      {clockTypeInput == "FLIP" && (<CornerFlipClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                      {clockTypeInput == "SIMPLE" && (<CornerSimpleClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                      {clockTypeInput == "DIGITAL" && (<CornerDigitalClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                      {clockTypeInput == "ANALOG" && (<CornerClassicClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                    </>
                    :
                    <>
                      <div className="corner-container" style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'transparent', overflow: 'hidden', }}                      >
                        <div className={`resizable-box `} onMouseDown={(e) => handleMouseDown(e, 'move')}
                          style={{
                            position: 'absolute',
                            left: `${area.start_margin}%`,
                            top: `${area.top_margin}%`,
                            width: `${area.width}%`,
                            height: `${area.height}%`,
                            backgroundColor: 'transparent',
                            border: `3px dotted #333`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'move',
                            overflow: 'hidden',
                          }}
                        >
                          {clockTypeInput == "FLIP" && (<CornerFlipClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                          {clockTypeInput == "SIMPLE" && (<CornerSimpleClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                          {clockTypeInput == "DIGITAL" && (<CornerDigitalClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}
                          {clockTypeInput == "ANALOG" && (<CornerClassicClock dimensions={dimensions} area={area} timestamp={timestamp} previewMode={true} />)}

                          <div className="resize-handle" style={{ bottom: -5, right: "50%", cursor: "ns-resize" }} onMouseDown={(e) => handleMouseDown(e, "bottom")}></div>
                          <div className="resize-handle" style={{ top: "50%", right: -5, cursor: "ew-resize" }} onMouseDown={(e) => handleMouseDown(e, "right")}></div>
                        </div>
                      </div>
                    </>
                  }
                </div>
              ) : (
                <div className="justify-content-center align-items-center align-self-center w-100 text-center">
                  <Spinner
                    variant="primary"
                    as="span"
                    animation="border"
                    size="lg"
                    role="status"
                    className="m-3"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
          </div>

          <div className=" overflow-y-auto scroll_div col-5 flex-wrap px-4 align-items-start justify-content-start py-3 border-start border-gray">
            <TitleInput control={control} errors={errors} />

            {/* COUNTRY AND TIMEZONE */}
            <div className="mb-2 col-12">
              <label className="form-label text-capitalize">{t('clock.country')}</label>
              <Controller
                name="country_code"
                control={control}
                rules={{ required: t('clock.country_required') }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    // disabled
                    id={field.name}
                    filter
                    {...field}
                    value={field.value}
                    options={COUNTRY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => field.onChange(e.value)}
                    placeholder={t('weather.select')}
                    className={`w-100 ${classNames({
                      "p-invalid": fieldState.invalid,
                    })}`}
                  />
                )}
              />
              {getFormErrorMessage("country_code", errors)}
            </div>

            {data?.zones?.length > 1 ? (
              <div className="mb-2 col-12">
                <label className="form-label text-capitalize">{t('clock.time_zone')}</label>
                <Controller
                  name="timeZone"
                  control={control}
                  rules={{ required: t('clock.time_zone_required') }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      id={field.name}
                      {...field}
                      value={field.value}
                      options={timeZoneData}
                      optionLabel="zoneName"
                      optionValue="zoneName"
                      onChange={(e) => {
                        field.onChange(e.value)
                      }}
                      placeholder={t('clock.select_time_zone')}
                      className={`w-100 ${classNames({
                        "p-invalid": fieldState.invalid,
                      })}`}
                    />
                  )}
                />
                {getFormErrorMessage("timeZone", errors)}
              </div>
            ) : (
              ""
            )}

            {/* CLOCK TYPE */}
            <div className="mb-2 col-12">
              <label className="form-label text-capitalize">{t('clock.clock_style')}</label>
              <Controller
                name="clockType"
                control={control}
                rules={{ required: t('clock.style_required') }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    // disabled
                    id={field.name}
                    {...field}
                    value={field.value}
                    options={CLOCK_TYPES}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => {
                      dispatch(setClockSettings({ clock_type: e.value }))
                      setArea(prev => ({
                        ...prev,
                        refresh: !prev.refresh ? 1 : 0
                      }));

                      if (e.value == "ANALOG") { dispatch(setClockSettings({ text_color: "#000" })) } else { dispatch(setClockSettings({ text_color: FLIP_CLOCK_SETTINGS.text_color })) }
                      field.onChange(e.value)
                    }}
                    placeholder="Select..."
                    className={`w-100 ${classNames({
                      "p-invalid": fieldState.invalid,
                    })}`}
                  />
                )}
              />
              {getFormErrorMessage("clockType", errors)}
            </div>

            {/* CLOCK FORMAT */}
            <div className="mb-2 col-12">
              <label className="form-label text-capitalize">{t('clock.time_format')}</label>
              <Controller
                name="timeFormat"
                control={control}
                rules={{ required: t('clock.time_format_required') }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    // disabled
                    id={field.name}
                    {...field}
                    value={field.value}
                    options={CLOCK_FORMAT}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => {
                      dispatch(setClockSettings({ timeFormat: e.value }))
                      field.onChange(e.value)
                    }}
                    placeholder="Select..."
                    className={`w-100 ${classNames({
                      "p-invalid": fieldState.invalid,
                    })}`}
                  />
                )}
              />
              {getFormErrorMessage("timeFormat", errors)}
            </div>

            {/* Display */}
            <div className="mb-2 col-12">
              <label className="form-label text-capitalize">{t('clock.display_type')}</label>
              <Controller
                name="display"
                control={control}
                rules={{ required: t('clock.display_required') }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    // disabled
                    id={field.name}
                    {...field}
                    value={field.value}
                    options={DISPLAY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => {
                      dispatch(setClockDisplay(e.value))
                      setArea(prev => ({
                        ...prev,
                        refresh: !prev.refresh ? 1 : 0
                      }));

                      field.onChange(e.value)
                    }}
                    placeholder="Select..."
                    className={`w-100 ${classNames({
                      "p-invalid": fieldState.invalid,
                    })}`}
                  />
                )}
              />
              {getFormErrorMessage("display", errors)}
            </div>

            {clockTypeInput == "FLIP" && (
              <div className="d-flex w-100 justify-between gap-3 ">
                {displayInput == "FULL_SCREEN" && (<BgColorInput />)}
                <TextColorInput />
                <CardColorInput />
              </div>)}

            {clockTypeInput == "ANALOG" && (
              <div className="d-flex w-100 justify-between gap-3 ">
                {displayInput == "FULL_SCREEN" && (<BgColorInput />)}

              </div>)}

            {clockTypeInput == "SIMPLE" ? (
              <div className="d-flex w-100 justify-between gap-3 ">
                {displayInput == "FULL_SCREEN" && (<BgColorInput />)}

                <TextColorInput />
              </div>) : ("")}

            {displayInput == "CORNER" ? (
              <CornerAreaControl area={area} setArea={setArea} />
            ) : (
              <></>
            )}
          </div>
        </div>

        <div className="col-12 text-center d-flex align-items-center justify-content-center py-4 border-top border-gray">
          <Button
            className="py-2 px-4 me-3 w-auto p-button-secondary p-button-sm"
            label={t('buttons.cancel')}
            disabled={createClock.isLoading || editClock.isLoading}
            onClick={onClose}
          />
          <Button
            type="submit"
            className="py-2 px-4 me-3 w-auto p-button-sm"
            label={isEdit ? t('buttons.publish') : t('buttons.create')}
            loading={createClock.isLoading || editClock.isLoading}
          />
        </div>
      </form >
    </Dialog >
  );
}

export default ClockDialog;
